<template>
  <hel-module title="采购金额趋势" :class="scope.clsPrefix" v-loading="loading">
    <div class="chart-container">
      <div id="purchaseAmountTrendChart" class="chart-body"></div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import * as echarts from 'echarts'
import { getPurchaseAmountTrend } from '@/api/procurement'

const MONTHS = [
  '1月', '2月', '3月', '4月', '5月', '6月',
  '7月', '8月', '9月', '10月', '11月', '12月'
]

export default {
  name: 'PurchaseAmountTrend',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-purchase-amount-trend'
      },
      year: '',
      actual: [],
      forecast: [],
      chartInstance: null
    }
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getPurchaseAmountTrend()
        if (!res || res.code !== 200 || !res.data) {
          this.actual = []
          this.forecast = []
          return
        }
        const d = res.data
        if (!Array.isArray(d.actual) || !d.actual.length) {
          this.actual = []
          this.forecast = []
          return
        }
        this.year = d.year || ''
        this.actual = d.actual
        this.forecast = Array.isArray(d.forecast) ? d.forecast : []
        this.$nextTick(() => {
          this.drawChart()
        })
      } catch (error) {
        console.error('获取采购金额趋势数据失败:', error)
        this.actual = []
        this.forecast = []
      } finally {
        this.loading = false
      }
    },
    drawChart() {
      const el = document.getElementById('purchaseAmountTrendChart')
      if (!el) return
      const chart = echarts.init(el)

      const actualCount = this.actual.length
      // 实际折线：仅有实际数据，预测月份留空
      const actualSeries = MONTHS.map((_, i) => (i < actualCount ? this.actual[i] : null))
      // 预测折线：从最后一个实际点衔接，之后取预测值
      const forecastSeries = MONTHS.map((_, i) => {
        if (i === actualCount - 1) return this.actual[actualCount - 1]
        if (i > actualCount - 1) {
          const v = this.forecast[i - actualCount]
          return v === undefined || v === null ? null : v
        }
        return null
      })
      const lastMonth = MONTHS[actualCount - 1]
      const lastValue = this.actual[actualCount - 1]
      // y 轴上限按实际与预测的最大值动态计算，避免真实数据超出固定刻度被截断
      const maxValue = Math.max(
        ...this.actual.filter((v) => v !== null && v !== undefined),
        ...this.forecast.filter((v) => v !== null && v !== undefined),
        0
      )
      const axisInterval = Math.max(100, Math.ceil(maxValue / 5 / 100) * 100)
      const axisMax = axisInterval * 5

      const option = {
        tooltip: {
          trigger: 'axis',
          confine: true,
          formatter: (params) => {
            const items = (Array.isArray(params) ? params : [params]).filter(
              (p) => p.value !== null && p.value !== undefined && p.value !== '-'
            )
            if (!items.length) return ''
            return items
              .map((p) => {
                const mm = String(p.dataIndex + 1).padStart(2, '0')
                if (p.seriesName === '采购预测') {
                  return `${this.year}-${mm} 采购预测 ${p.value} 万元（供需计划智能体口径：近 6 完整月去巅均值）`
                }
                return `${this.year}-${mm} 实际采购额 ${p.value} 万元`
              })
              .join('<br/>')
          }
        },
        grid: {
          top: 34,
          left: 10,
          right: 14,
          bottom: 6,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: MONTHS,
          axisLine: {
            lineStyle: {
              color: '#edf1f7'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            interval: 0,
            color: '#6b7f9a',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: axisMax,
          interval: axisInterval,
          name: '金额（万元）',
          nameLocation: 'end',
          nameGap: 16,
          nameTextStyle: {
            color: '#8aa0c0',
            fontSize: 12,
            align: 'left'
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#edf1f7'
            }
          },
          axisLabel: {
            color: '#6b7f9a',
            fontSize: 12,
            formatter: (value) => (value >= 1000 ? value.toLocaleString('en-US') : value)
          }
        },
        series: [
          {
            name: '实际采购额',
            type: 'line',
            symbol: 'circle',
            symbolSize: 9,
            itemStyle: {
              color: '#fff',
              borderColor: '#4a5ff3',
              borderWidth: 3
            },
            lineStyle: {
              color: '#4a5ff3',
              width: 3
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(74, 95, 243, 0.26)' },
                { offset: 1, color: 'rgba(74, 95, 243, 0)' }
              ])
            },
            data: actualSeries,
            markPoint: {
              silent: true,
              symbol: 'circle',
              symbolSize: [1, 1],
              itemStyle: {
                color: 'transparent'
              },
              data: [
                {
                  coord: [lastMonth, lastValue],
                  label: {
                    show: true,
                    position: 'top',
                    distance: 14,
                    formatter: `${lastValue}万`,
                    color: '#fff',
                    backgroundColor: '#4a5ff3',
                    fontSize: 16,
                    fontWeight: 700,
                    borderRadius: 7,
                    padding: [6, 10]
                  }
                }
              ]
            }
          },
          {
            name: '采购预测',
            type: 'line',
            symbol: 'circle',
            symbolSize: 9,
            itemStyle: {
              color: '#fff',
              borderColor: '#4a5ff3',
              borderWidth: 2.5,
              borderType: [3, 3]
            },
            lineStyle: {
              color: '#4a5ff3',
              width: 3,
              type: [8, 6],
              opacity: 0.85
            },
            data: forecastSeries
          }
        ]
      }

      chart.setOption(option)
      this.chartInstance = chart
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartInstance && this.chartInstance.resize()
        })
        setTimeout(() => {
          this.chartInstance && this.chartInstance.resize()
        }, 200)
      })
    },
    handleResize() {
      if (this.chartInstance) {
        setTimeout(() => {
          this.chartInstance.resize()
        }, 200)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-purchase-amount-trend";
.#{$class-prefix} {
  .chart-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 4px 0 0;
    box-sizing: border-box;
  }
  .chart-body {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 240px;
  }
}
</style>
